import axios, { AxiosInstance } from 'axios';

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_EMAIL_KEY = 'auth_email';

export interface AuthUser {
    email: string;
    token: string;
    role?: string;
}

export interface RequestMagicLinkResponse {
    success: boolean;
    message: string;
}

export interface ValidateTokenResponse {
    valid: boolean;
    email: string;
    token: string; // Refreshed token
}

export interface VerifySessionResponse {
    valid: boolean;
    user?: {
        email: string;
        role: string;
    };
}

class AuthService {
    private client: AxiosInstance;

    constructor() {
        this.client = axios.create({
            baseURL: import.meta.env.VITE_SERVER_URL || '',
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
            },
            validateStatus: function (status) {
                return status < 500; // Resolve only if the status code is less than 500 (i.e., include 4xx codes)
            },
        });
    }

    // Get stored auth token
    getToken(): string | null {
        return localStorage.getItem(AUTH_TOKEN_KEY);
    }

    // Get stored email
    getEmail(): string | null {
        return localStorage.getItem(AUTH_EMAIL_KEY);
    }

    // Check if user is authenticated (has token)
    isAuthenticated(): boolean {
        return !!this.getToken();
    }

    // Save auth data to localStorage
    saveAuth(email: string, token: string): void {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem(AUTH_EMAIL_KEY, email);
    }

    // Clear auth data from localStorage
    clearAuth(): void {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_EMAIL_KEY);
    }

    // Request a magic link to be sent to the email
    async requestMagicLink(email: string): Promise<RequestMagicLinkResponse> {
        const response = await this.client.post<RequestMagicLinkResponse>('/api/auth/request-magic-link', { email });
        return response.data;
    }

    // Validate a magic link token (called when user clicks the link)
    async validateToken(token: string): Promise<ValidateTokenResponse> {
        const response = await this.client.post<ValidateTokenResponse>('/api/auth/validate-token', { token });
        return response.data;
    }

    // Verify current session is still valid
    async verifySession(): Promise<{ valid: boolean; user?: { email: string; role: string } }> {
        const token = this.getToken();
        if (!token) return { valid: false };

        try {
            const response = await this.client.post<VerifySessionResponse>('/api/auth/verify-session', { token });
            if (!response.data.valid) {
                // Only clear auth if the server explicitly says the session is invalid
                this.clearAuth();
            }
            return {
                valid: response.data.valid,
                user: response.data.user,
            };
        } catch {
            // Network errors (server not running, timeout) should not clear auth
            return { valid: false };
        }
    }

    // Logout
    logout(): void {
        this.clearAuth();
    }
}

// Export singleton instance
export const authService = new AuthService();
